// Order Transfer System for SECRET Program
// Secure order transfers between users with 24-hour expiring tokens

import { supabase } from './supabase'
import { notifyTransferReceived } from './notifications'
import { logTransferCreated, logTransferClaimed, logTransferCancelled } from './auditLog'

export interface TransferRequest {
  orderId: string
  fromUserId: string
  toContact: string // email or phone
  personalMessage?: string
}

export interface TransferToken {
  token: string
  expiresAt: Date
  transferLogId: string
}

export interface TransferClaim {
  transferToken: string
  newUserData?: {
    firstName: string
    lastName: string
    zipCode: string
  }
}

export interface TransferStatus {
  isValid: boolean
  isExpired: boolean
  isClaimed: boolean
  order?: any
  fromUser?: any
  error?: string
}

// Generate secure transfer token
export const generateTransferToken = (): string => {
  // Generate cryptographically secure random token using Web Crypto API
  const array = new Uint8Array(32)
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array)
  } else {
    // Fallback for server-side or older browsers
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256)
    }
  }
  
  // Convert to base64url
  const base64 = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
  
  return `SECRET_${base64}`
}

// Create order transfer
export const createOrderTransfer = async (request: TransferRequest): Promise<TransferToken> => {
  try {
    // Validate order belongs to user and can be transferred
    const { data: order, error: orderError } = await supabase
      .from('secret_orders')
      .select(`
        *,
        secret_events (title, location_name, event_date)
      `)
      .eq('id', request.orderId)
      .eq('user_id', request.fromUserId)
      .single()

    if (orderError || !order) {
      throw new Error('Order not found or access denied')
    }

    // Check if order can be transferred
    if (!['confirmed', 'preparing'].includes(order.status)) {
      throw new Error(`Cannot transfer order with status: ${order.status}`)
    }

    // Check if transfer already exists and is active
    const { data: existingTransfer } = await supabase
      .from('secret_transfer_logs')
      .select('*')
      .eq('order_id', request.orderId)
      .eq('status', 'pending')
      .single()

    if (existingTransfer) {
      throw new Error('Transfer already in progress for this order')
    }

    // Generate secure token
    const token = generateTransferToken()
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours

    // Create transfer log entry
    const { data: transferLog, error: transferError } = await supabase
      .from('secret_transfer_logs')
      .insert({
        order_id: request.orderId,
        from_user_id: request.fromUserId,
        to_contact: request.toContact,
        transfer_token: token,
        expires_at: expiresAt.toISOString(),
        status: 'pending'
      })
      .select()
      .single()

    if (transferError || !transferLog) {
      throw new Error('Failed to create transfer log')
    }

    // Update order with transfer token
    await supabase
      .from('secret_orders')
      .update({
        transfer_token: token,
        transfer_expires_at: expiresAt.toISOString()
      })
      .eq('id', request.orderId)

    // Log the transfer creation
    await logTransferCreated({
      userId: request.fromUserId,
      orderId: request.orderId,
      transferToken: token,
      recipientInfo: request.toContact,
      expiresAt: expiresAt.toISOString()
    })

    return {
      token,
      expiresAt,
      transferLogId: transferLog.id
    }

  } catch (error) {
    console.error('Failed to create order transfer:', error)
    throw error
  }
}

// Validate transfer token
export const validateTransferToken = async (token: string): Promise<TransferStatus> => {
  try {
    const { data: transferLog, error } = await supabase
      .from('secret_transfer_logs')
      .select(`
        *,
        secret_orders (
          *,
          secret_events (title, location_name, event_date),
          secret_order_items (
            *,
            secret_menu_items (name, price)
          )
        ),
        secret_users!secret_transfer_logs_from_user_id_fkey (
          first_name,
          last_name,
          email,
          phone
        )
      `)
      .eq('transfer_token', token)
      .single()

    if (error || !transferLog) {
      return { isValid: false, isExpired: false, isClaimed: false, error: 'Invalid transfer token' }
    }

    const now = new Date()
    const expiresAt = new Date(transferLog.expires_at)
    const isExpired = now > expiresAt
    const isClaimed = transferLog.status === 'claimed'

    if (isExpired) {
      // Auto-expire the transfer
      await supabase
        .from('secret_transfer_logs')
        .update({ status: 'expired' })
        .eq('transfer_token', token)

      return { isValid: false, isExpired: true, isClaimed: false, error: 'Transfer token has expired' }
    }

    if (isClaimed) {
      return { isValid: false, isExpired: false, isClaimed: true, error: 'Transfer has already been claimed' }
    }

    return {
      isValid: true,
      isExpired: false,
      isClaimed: false,
      order: transferLog.secret_orders,
      fromUser: transferLog.secret_users
    }

  } catch (error) {
    console.error('Failed to validate transfer token:', error)
    return { isValid: false, isExpired: false, isClaimed: false, error: 'Validation failed' }
  }
}

// Claim transferred order
export const claimTransferredOrder = async (
  claim: TransferClaim, 
  claimingUserId?: string
): Promise<{ success: boolean; orderId?: string; error?: string }> => {
  try {
    // Validate token first
    const validation = await validateTransferToken(claim.transferToken)
    if (!validation.isValid) {
      return { success: false, error: validation.error }
    }

    const { order, fromUser } = validation

    // Get transfer log
    const { data: transferLog } = await supabase
      .from('secret_transfer_logs')
      .select('*')
      .eq('transfer_token', claim.transferToken)
      .single()

    if (!transferLog) {
      return { success: false, error: 'Transfer not found' }
    }

    let recipientUserId = claimingUserId

    // If no user is logged in, check if user exists by contact info
    if (!recipientUserId) {
      const contact = transferLog.to_contact
      const isEmail = contact.includes('@')
      
      const { data: existingUser } = await supabase
        .from('secret_users')
        .select('id')
        .eq(isEmail ? 'email' : 'phone', contact)
        .single()

      if (existingUser) {
        recipientUserId = existingUser.id
      } else if (claim.newUserData) {
        // Create new user account
        const { data: newUser, error: createError } = await supabase
          .from('secret_users')
          .insert({
            [isEmail ? 'email' : 'phone']: contact,
            first_name: claim.newUserData.firstName,
            last_name: claim.newUserData.lastName,
            zip_code: claim.newUserData.zipCode,
            [isEmail ? 'email_verified' : 'phone_verified']: false,
            is_active: true
          })
          .select('id')
          .single()

        if (createError || !newUser) {
          return { success: false, error: 'Failed to create user account' }
        }

        recipientUserId = newUser.id
      } else {
        return { success: false, error: 'User account required to claim transfer' }
      }
    }

    // Transfer the order
    const { error: updateError } = await supabase
      .from('secret_orders')
      .update({
        user_id: recipientUserId,
        status: 'transferred',
        transferred_to: recipientUserId,
        transferred_at: new Date().toISOString(),
        transfer_token: null,
        transfer_expires_at: null
      })
      .eq('id', order.id)

    if (updateError) {
      return { success: false, error: 'Failed to transfer order' }
    }

    // Update transfer log
    await supabase
      .from('secret_transfer_logs')
      .update({
        to_user_id: recipientUserId,
        status: 'claimed',
        claimed_at: new Date().toISOString()
      })
      .eq('transfer_token', claim.transferToken)

    // Log the transfer claim
    await logTransferClaimed({
      newUserId: recipientUserId,
      orderId: order.id,
      transferToken: claim.transferToken,
      claimedBy: claimingUserId ? 'existing_user' : 'new_user',
      originalUserId: fromUser.id
    })

    // Send notification to recipient
    try {
      await notifyTransferReceived(recipientUserId, {
        orderNumber: order.order_number,
        fromName: `${fromUser.first_name} ${fromUser.last_name}`,
        eventTitle: order.secret_events?.title,
        orderId: order.id
      })
    } catch (notificationError) {
      console.error('Failed to send transfer notification:', notificationError)
      // Don't fail the transfer if notification fails
    }

    return { success: true, orderId: order.id }

  } catch (error) {
    console.error('Failed to claim transferred order:', error)
    return { success: false, error: 'Transfer claim failed' }
  }
}

// Cancel transfer
export const cancelTransfer = async (orderId: string, userId: string): Promise<boolean> => {
  try {
    // Verify order belongs to user
    const { data: order } = await supabase
      .from('secret_orders')
      .select('id, user_id, transfer_token')
      .eq('id', orderId)
      .eq('user_id', userId)
      .single()

    if (!order || !order.transfer_token) {
      return false
    }

    // Cancel transfer log
    await supabase
      .from('secret_transfer_logs')
      .update({ status: 'cancelled' })
      .eq('order_id', orderId)
      .eq('status', 'pending')

    // Log the transfer cancellation
    await logTransferCancelled({
      userId,
      orderId,
      transferToken: order.transfer_token,
      reason: 'user_cancelled'
    })

    // Remove transfer token from order
    await supabase
      .from('secret_orders')
      .update({
        transfer_token: null,
        transfer_expires_at: null
      })
      .eq('id', orderId)

    return true

  } catch (error) {
    console.error('Failed to cancel transfer:', error)
    return false
  }
}

// Get transfer history for user
export const getUserTransferHistory = async (userId: string) => {
  try {
    const { data: transfers, error } = await supabase
      .from('secret_transfer_logs')
      .select(`
        *,
        secret_orders (
          order_number,
          total_amount,
          secret_events (title, event_date)
        ),
        from_user:secret_users!secret_transfer_logs_from_user_id_fkey (
          first_name,
          last_name
        ),
        to_user:secret_users!secret_transfer_logs_to_user_id_fkey (
          first_name,
          last_name
        )
      `)
      .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)
      .order('created_at', { ascending: false })

    if (error) throw error

    return transfers || []

  } catch (error) {
    console.error('Failed to get transfer history:', error)
    return []
  }
}

// Generate transfer link
export const generateTransferLink = (token: string): string => {
  const baseUrl = typeof window !== 'undefined' 
    ? window.location.origin 
    : 'https://secret.tailtations.com'
  
  return `${baseUrl}/transfer/${token}`
}

// Get transfer sharing text
export const getTransferSharingText = (order: any, fromUser: any, transferLink: string): { sms: string; email: string } => {
  const eventTitle = order.secret_events?.title || 'SECRET Event'
  const fromName = `${fromUser.first_name} ${fromUser.last_name}`
  
  return {
    sms: `🦞 ${fromName} is transferring their SECRET order (${order.order_number}) for "${eventTitle}" to you! Claim it here: ${transferLink} (expires in 24 hours)`,
    email: `${fromName} has transferred their SECRET order to you! Order #${order.order_number} for "${eventTitle}" - Total: $${order.total_amount}. Claim your order: ${transferLink} (Link expires in 24 hours)`
  }
}

// Cleanup expired transfers (should be run periodically)
export const cleanupExpiredTransfers = async (): Promise<number> => {
  try {
    const now = new Date()
    
    // Get expired transfers
    const { data: expiredTransfers, error: selectError } = await supabase
      .from('secret_transfer_logs')
      .select('order_id, transfer_token')
      .eq('status', 'pending')
      .lt('expires_at', now.toISOString())

    if (selectError) throw selectError

    if (!expiredTransfers || expiredTransfers.length === 0) {
      return 0
    }

    // Update transfer logs
    await supabase
      .from('secret_transfer_logs')
      .update({ status: 'expired' })
      .eq('status', 'pending')
      .lt('expires_at', now.toISOString())

    // Remove transfer tokens from orders
    const orderIds = expiredTransfers.map(t => t.order_id)
    await supabase
      .from('secret_orders')
      .update({
        transfer_token: null,
        transfer_expires_at: null
      })
      .in('id', orderIds)

    return expiredTransfers.length

  } catch (error) {
    console.error('Failed to cleanup expired transfers:', error)
    return 0
  }
}

// Validate contact format
export const validateTransferContact = (contact: string): { isValid: boolean; type: 'email' | 'phone' | null; error?: string } => {
  if (!contact || contact.trim().length === 0) {
    return { isValid: false, type: null, error: 'Contact is required' }
  }

  const trimmed = contact.trim()

  // Check if it's an email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (emailRegex.test(trimmed)) {
    return { isValid: true, type: 'email' }
  }

  // Check if it's a phone number (basic validation)
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
  const digitsOnly = trimmed.replace(/[\s\-\(\)\.]/g, '')
  
  if (phoneRegex.test(digitsOnly) && digitsOnly.length >= 10) {
    return { isValid: true, type: 'phone' }
  }

  return { isValid: false, type: null, error: 'Please enter a valid email address or phone number' }
}